import { useState } from "react";
import { UseFormSetValue } from "react-hook-form";
import { taskService } from "@miplatform/api";
import { HTTP_STATUSES, ORDER_TYPES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import { IAddRequirementsData } from "../../schema/addRequirements.schema";

type RequirementStep = { stepTemplateId: string; workBaseTemplateId: string };
export type OrderTemplateTab = { id: string; name: string };

export const useRequirementStepsViewModel = ({
  steps,
  setValue,
}: {
  steps: RequirementStep[];
  setValue: UseFormSetValue<IAddRequirementsData>;
}) => {
  const stateManager = useComponentState();
  const [orderTemplateTabs, setOrderTemplateTabs] = useState<
    OrderTemplateTab[]
  >([]);
  const [allSteps, setAllSteps] = useState<{ id: string; name: string }[]>([]);
  const [selectedTab, setSelectedTab] = useState<OrderTemplateTab | null>(null);

  const loadTabs = () => {
    stateManager.run(async () => {
      const response = await taskService.orderTemplates.getAll({
        filters: {
          homePhases: { any: ORDER_TYPES.UNDER_CONSTRUCTION },
          fulfillmentSequence: { ne: null },
        },
        sort: "fulfillmentSequence asc",
      });
      if (response.status === HTTP_STATUSES.OK) {
        const tabs: OrderTemplateTab[] = response.data.map((t: any) => ({
          id: t.id,
          name: t.shortName ?? t.name,
        }));
        setOrderTemplateTabs(tabs);
        if (tabs.length > 0) {
          setSelectedTab(tabs[0]);
          loadStepsForTab(tabs[0].id);
        }
      }
    });
  };

  const loadStepsForTab = (workBaseTemplateId: string) => {
    stateManager.run(async () => {
      const response = await taskService.stepTemplates.getByModule({
        module: "MiInspections",
        filters: {
          workBaseTemplateId: {
            eq: { type: "guid", value: workBaseTemplateId },
          },
        },
        sort: "title asc",
      });
      if (response.status === HTTP_STATUSES.OK) {
        setAllSteps(
          response.data.map((s: any) => ({ id: s.id, name: s.title }))
        );
      }
    });
  };

  const handleTabChange = (tab: OrderTemplateTab) => {
    setSelectedTab(tab);
    setAllSteps([]);
    loadStepsForTab(tab.id);
  };

  const getSelectedStepIds = (workBaseTemplateId: string): string[] =>
    steps
      .filter((s) => s.workBaseTemplateId === workBaseTemplateId)
      .map((s) => s.stepTemplateId);

  const handleAddStep = (
    workBaseTemplateId: string,
    stepTemplateId: string
  ) => {
    setValue("steps", [...steps, { stepTemplateId, workBaseTemplateId }], {
      shouldDirty: true,
    });
  };

  const handleRemoveStep = (
    workBaseTemplateId: string,
    stepTemplateId: string
  ) => {
    setValue(
      "steps",
      steps.filter(
        (s) =>
          !(
            s.stepTemplateId === stepTemplateId &&
            s.workBaseTemplateId === workBaseTemplateId
          )
      ),
      { shouldDirty: true }
    );
  };

  return {
    isLoading: stateManager.isBusy(),
    orderTemplateTabs,
    allSteps,
    selectedTab,
    loadTabs,
    getSelectedStepIds,
    handleAddStep,
    handleRemoveStep,
    handleTabChange,
  };
};
